import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { CheckCircle, Loader2, XCircle, Coins } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreditSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const creditType = params.get("type") || "electrical";
  const [status, setStatus] = useState("verifying");
  const [creditsAdded, setCreditsAdded] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let retries = 0;
    const maxRetries = 15;
    const verify = async () => {
      try {
        const res = await fetch(`${API}/api/credits/verify/${sessionId}`, { credentials: "include" });
        if (!res.ok) { setStatus("error"); return; }
        const data = await res.json();
        if (data.status === "completed") {
          setCreditsAdded(data.credits_added);
          setStatus("success");
        } else if (retries < maxRetries) {
          retries++;
          setTimeout(verify, 2000);
        } else {
          setStatus("error");
        }
      } catch {
        if (retries < 3) {
          retries++;
          setTimeout(verify, 2000);
        } else {
          setStatus("error");
        }
      }
    };
    verify();
  }, [sessionId]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}>
      <AppHeader />
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        {status === "verifying" && (
          <div className="form-container" data-testid="credit-verifying">
            <Loader2 className="w-12 h-12 text-[#D4A537] animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Verifying Payment...</h2>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Please wait while we confirm your purchase.</p>
          </div>
        )}
        {status === "success" && (
          <div className="form-container" data-testid="credit-success">
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Credits Added!</h2>
            <div className="flex items-center justify-center gap-2 my-4">
              <Coins className="w-6 h-6 text-[#D4A537]" />
              <span className="text-3xl font-bold text-[#D4A537]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>+{creditsAdded}</span>
              <span className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{creditType} credits</span>
            </div>
            {creditsAdded > 20 && (
              <p className="text-sm text-emerald-600 font-medium mb-4">Includes first purchase bonus of 10 credits!</p>
            )}
            <Link to="/dashboard" className="brutalist-btn inline-flex items-center gap-2">
              Go to Dashboard
            </Link>
          </div>
        )}
        {status === "error" && (
          <div className="form-container" data-testid="credit-error">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Payment Verification Issue</h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>We couldn't verify your payment. If you were charged, your credits will be added automatically. If not, please try again.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/dashboard" className="brutalist-btn inline-flex items-center gap-2">Go to Dashboard</Link>
              <Link to="/contact" className="brutalist-btn-outline inline-flex items-center gap-2">Contact Support</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
